import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "$root/components/common/SidePanelV2";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { Divider } from "$root/components/Divider";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueriesWrapperBase } from "$root/components/ReactQueryWrapper";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Column, FlexBox, Icon } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, useAtomValue, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import { useForm } from "react-hook-form";
import { workspaceChainPredicateSubmitShape, WorkspaceFilterChain } from "./chain";
import { workspaceChainPredicateModel } from "./chain/type";
import { workspaceRepository } from "./repository";
import { SidePanelListItem } from "./ui/SidePanelItem";
import { selectConcept, useWorkspaceDispatch, useWorkspaceStore } from "./state";
import { autoGenerateMetricName } from "./common";
import { conceptFormModel, conceptFormSubmitSchema, formFromConcept } from "./concept-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type z from "zod";
import { isCustomAttributeConceptId } from "./services/sharedConceptService";

export function WorkspaceFilterSettingsStack() {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const { readState, slices } = useWorkspaceStore();
	const workspaceConceptsMap = workspaceRepository.getConceptMapDefinitions.useQuery();
	const workspaceTagConcepts = workspaceRepository.getTagConceptDefinitions.useQuery();

	const dispatch = useWorkspaceDispatch();
	const filters = useAtomValue(slices.filters$);

	return (
		<>
			<SidePanelHeader
				title="Filter"
				icon={"Filter-no"}
				description="Multiselect sorting rules"
				primaryAction={{
					label: "Add filter",
					onClick() {
						sidePanelCtx.push(() => <WorkspaceCreateFilterStack title="Add filter" />);
					},
				}}
			/>
			<Divider />
			{filters.length > 0 ? (
				<>
					<SidePanelContent className="flex-1 min-h-0 overflow-y-auto">
						<FlexBox direction="column" className={toClassName("overflow-hidden")}>
							<ReactQueriesWrapperBase queries={[workspaceConceptsMap, workspaceTagConcepts]}>
								{([workspaceMap, availableTagsConcepts]) => (
									<WithAtom atom$={slices.filters$}>
										{(filters) => (
											<ForEach collection={filters}>
												{(item) => {
													// `conceptReferencesResolve` is enforced, so a filter's concept is in the pool; the branch
													// that rendered nothing for it was guarding a state ingestion now repairs.
													const parametrizedConcept =
														selectConcept(readState(), item.parameterizedConceptUuid) ?? unreachable();

													const concept = workspaceMap.get(parametrizedConcept.conceptId);

													if (!concept) {
														return <></>;
													}

													return (
														<>
															<SidePanelListItem
																title={parametrizedConcept.label}
																description={concept.description}
																onDrillDown={() => {
																	sidePanelCtx.push(() => (
																		<WorkspaceEditFilterStack
																			id={item.parameterizedConceptUuid}
																			title={parametrizedConcept.label}
																			description={concept.description}
																			initialValue={{
																				...formFromConcept(parametrizedConcept, concept, {
																					tagConcepts: availableTagsConcepts,
																				}),
																				// The filter's own half: the persisted condition is the *filter's*, not the
																				// concept's, so it is spread on rather than folded into the translation.
																				comparisonMode: item.keyword,
																				comparisonValue: item.arguments as [any],
																			}}
																		/>
																	));
																}}
																overlayActions={[
																	{
																		icon: "Delete",
																		onClick: () =>
																			void dispatch({
																				type: "removeFilter",
																				parameterizedConceptUuid: parametrizedConcept.uuid,
																			}),
																	},
																]}
															/>
															<Divider className="border-dashed" />
														</>
													);
												}}
											</ForEach>
										)}
									</WithAtom>
								)}
							</ReactQueriesWrapperBase>
						</FlexBox>
					</SidePanelContent>
					<SidePanelFooter
						neutralAction={() => {
							return (
								<Button
									palette="secondary"
									size="small"
									className="gap-1"
									onClick={() => {
										void dispatch({ type: "removeAllFilters" });
									}}
								>
									<Icon icon="Delete" size={16} /> Clear All
								</Button>
							);
						}}
					/>
				</>
			) : (
				<SidePanelContent className="flex-1">
					<IconWalls.WorkspaceMissingFilters
						onClick={() => sidePanelCtx.push(() => <WorkspaceCreateFilterStack title="Add filter" />)}
					/>
				</SidePanelContent>
			)}
		</>
	);
}

/**
 * The filter panels' form model: a chain, the condition it is filtered by, and the name checkbox — i.e.
 * `FilterWorkspaceChain` plus the one field a panel owns.
 *
 * Derived from the model, like every form type in this area, so the resolver below cannot fail to declare one
 * of its fields: `autoGenerateName` was in fact missing from it and was being deleted on the way to submit,
 * which cost nothing only because it is the one field no payload reads.
 */
const workspaceFilterFormModel = conceptFormModel.merge(workspaceChainPredicateModel);

type WorkspaceFilter = z.infer<typeof workspaceFilterFormModel>;

/**
 * Both filter panels resolve with this. They used to resolve with two schemas that differed by a redundant
 * second `name: z.string()` on the create side — the chain's own schema has always required one — so the
 * difference was never a difference in what either panel accepted.
 */
export const workspaceFilterResolverSchema = conceptFormSubmitSchema(
	workspaceFilterFormModel.extend(workspaceChainPredicateSubmitShape),
);

export function WorkspaceEditFilterStack(props: {
	id: string;
	title: string;
	description?: string;
	initialValue?: Partial<WorkspaceFilter>;
}): React.JSX.Element {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const dispatch = useWorkspaceDispatch();

	const form = useForm<WorkspaceFilter>({
		defaultValues: { autoGenerateName: true, ...(props.initialValue ?? {}) },
		resolver: zodResolver(workspaceFilterResolverSchema),
	});
	const autogenerateName = form.watch("autoGenerateName");

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={{
					label: "Update",
					async onClickAsync() {
						await handleSubmitToCustomSubmitHandler({
							handleSubmit: form.handleSubmit,
							baseSubmitFn: (workspaceConcept) => {
								void dispatch({ type: "editFilter", parameterizedConceptUuid: props.id, chain: workspaceConcept });
								sidePanelCtx.pop();
							},
						})();
					},
				}}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<Column gap={16}>
					<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
						<WorkspaceFilterChain
							initialValue={props.initialValue}
							autogenerateName={autogenerateName}
							onChange={(newWorkspaceChain) => {
								form.reset({
									...newWorkspaceChain,
									autoGenerateName: form.getValues("autoGenerateName"),
								});
							}}
						/>
					</div>
					<Divider />
					<Column gap={8}>
						<FormFields.Text
							name="name"
							control={form.control}
							formState={form.formState}
							label="Name"
							disabled={form.watch("autoGenerateName")}
						/>
						<FormFields.Checkbox
							name="autoGenerateName"
							control={form.control}
							formState={form.formState}
							onChange={(shouldAutogenerate) => {
								if (shouldAutogenerate) {
									form.reset({
										...form.getValues(),
										name: autoGenerateMetricName(form.getValues()),
									});
								}
							}}
						>
							Auto-generated
						</FormFields.Checkbox>
					</Column>
				</Column>
			</SidePanelContent>
			<SidePanelFooter
				className={"z-10"}
				neutralAction={() => {
					return (
						<Button
							palette="secondary"
							size="small"
							className="gap-1"
							onClick={() => {
								void dispatch({ type: "removeFilter", parameterizedConceptUuid: props.id });
								sidePanelCtx.pop();
							}}
						>
							<Icon icon="Delete" size={16} /> Delete
						</Button>
					);
				}}
			/>
		</>
	);
}

export type WorkspaceCreateFilterForm = WorkspaceFilter;

export function WorkspaceCreateFilterStack(props: {
	title: string;
	description?: string;
	initialValue?: Partial<WorkspaceFilter>;
}) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const { dispatch, slices } = useWorkspaceStore();
	const form = useForm<WorkspaceCreateFilterForm>({
		defaultValues: { autoGenerateName: true, ...props.initialValue },
		resolver: zodResolver(workspaceFilterResolverSchema),
	});

	const autogenerateName = form.watch("autoGenerateName");

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={{
					label: "Apply",
					async onClickAsync() {
						await handleSubmitToCustomSubmitHandler({
							handleSubmit: form.handleSubmit,
							baseSubmitFn: (workspaceConcept) => {
								const conceptId = workspaceConcept.concept?.id;
								if (workspaceConcept.concept?.shared && conceptId && isCustomAttributeConceptId(conceptId)) {
									const sharedParametrizedConcept = readAtom(slices.sharedConcepts$).find(
										(x) => x.conceptId === conceptId,
									);

									if (!sharedParametrizedConcept) {
										throw new Error("Unable to find matching sharedParametrizedConcept");
									}

									// The *concept*, not its uuid. A filter used to be created against a uuid this panel had resolved
									// and the operation stored nothing, which is how a filter could end up naming a concept the
									// workspace did not hold; given the concept, the pool can take it.
									void dispatch({ type: "addFilter", chain: workspaceConcept, concept: sharedParametrizedConcept });
									sidePanelCtx.pop();
									return;
								}
								void dispatch({ type: "addFilter", chain: workspaceConcept });
								sidePanelCtx.pop();
							},
						})();
					},
				}}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<Column gap={16}>
					<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
						<WorkspaceFilterChain
							initialValue={props.initialValue}
							autogenerateName={autogenerateName}
							onChange={(newWorkspaceChain) => {
								form.reset({
									...newWorkspaceChain,
									autoGenerateName: form.getValues("autoGenerateName"),
								});
							}}
						/>
					</div>
					<Divider />
					<Column gap={8}>
						<FormFields.Text
							name="name"
							control={form.control}
							formState={form.formState}
							label="Name"
							disabled={form.watch("autoGenerateName")}
						/>
						<FormFields.Checkbox
							name="autoGenerateName"
							control={form.control}
							formState={form.formState}
							onChange={(shouldAutogenerate) => {
								if (shouldAutogenerate) {
									form.reset({
										...form.getValues(),
										name: autoGenerateMetricName(form.getValues()),
									});
								}
							}}
						>
							Auto-generated
						</FormFields.Checkbox>
					</Column>
				</Column>
			</SidePanelContent>
		</>
	);
}
